import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { v4 as uuid } from 'uuid'

import { useDisableMainScroll } from '@hooks/useDisableMainScroll'

import type { ModalContainerMode } from './ModalInstanceContext'

export interface BaseModalProps {
  id: string
  initialIsOpen?: boolean
  close: () => Promise<void>
  onClose: () => void
}

export type BaseModalType<P = {}> = React.FC<BaseModalProps & P> & {
  containerMode?: ModalContainerMode
}

type StoredProps = Record<string, unknown>

export interface ModalData {
  id: string
  component: BaseModalType<any>
  onClose?: () => void
  close: () => Promise<void>
  props?: StoredProps
  isClosing?: boolean
}

export type OpenModalAction<P = unknown> = {
  id?: string
  component: BaseModalType<P>
  onClose?: () => void
  props?: StoredProps
}

export type ModalUserProps<M extends BaseModalType<any>> = M extends BaseModalType<infer P>
  ? P
  : never

export type ModalOpenProps<M extends BaseModalType<any>> = Omit<BaseModalProps, 'id'> &
  ModalUserProps<M>

interface ModalAction {
  type: 'OPEN' | 'REQUEST_CLOSE' | 'FINALIZE_CLOSE' | 'UPDATE' | 'CLOSE_ALL'
  modal?: ModalData
  requestId?: string
  removeId?: string
  updateId?: string
  updateProps?: StoredProps
}

type RequestCloseHandler = () => Promise<void> | void

const waitForStateCommit = () => new Promise<void>((resolve) => window.setTimeout(resolve, 0))

interface ModalContextValue {
  modals: ModalData[]
  open: <P>(modal: OpenModalAction<P>) => string

  close: (id: string) => Promise<void>
  closeAll: () => void
  startClose: (id: string) => boolean

  finalizeClose: (id: string) => Promise<void>
  registerRequestClose: (id: string, fn: RequestCloseHandler | null) => void
  update: (id: string, props: StoredProps) => void
}

const ModalsContext = createContext<ModalContextValue | undefined>(undefined)

interface ModalsContextProviderProps extends PropsWithChildren {
  defaultModals?: ModalData[]
}

export const ModalsContextProvider: FC<ModalsContextProviderProps> = ({
  children,
  defaultModals = [],
}) => {
  const [modals, dispatch] = useReducer(reducer, defaultModals)
  const { disableMainScroll, enableMainScroll } = useDisableMainScroll()

  const modalsRef = useRef<ModalData[]>(modals)
  useEffect(() => {
    modalsRef.current = modals
  }, [modals])

  useEffect(() => {
    if (modals.length > 0) disableMainScroll()
    else enableMainScroll()
  }, [disableMainScroll, enableMainScroll, modals.length])

  const finalizedRef = useRef<Record<string, true>>({})
  const requestCloseHandlersRef = useRef<Record<string, RequestCloseHandler | undefined>>({})

  const closeRequestedRef = useRef<Record<string, true>>({})
  const closePromisesRef = useRef<Record<string, Promise<void> | undefined>>({})

  const registerRequestClose = useCallback((id: string, fn: RequestCloseHandler | null) => {
    if (fn) requestCloseHandlersRef.current[id] = fn
    else delete requestCloseHandlersRef.current[id]
  }, [])

  const finalizeClose = useCallback(async (id: string) => {
    if (finalizedRef.current[id]) return
    finalizedRef.current[id] = true

    delete closeRequestedRef.current[id]
    delete closePromisesRef.current[id]
    delete requestCloseHandlersRef.current[id]

    const modalToClose = modalsRef.current.find((m) => m.id === id)
    modalToClose?.onClose?.()

    dispatch({ type: 'FINALIZE_CLOSE', removeId: id })
    await waitForStateCommit()
  }, [])

  const startClose = useCallback((id: string) => {
    const modalToClose = modalsRef.current.find((m) => m.id === id)
    if (!modalToClose) return false
    if (modalToClose.isClosing) return false
    if (finalizedRef.current[id]) return false
    if (closeRequestedRef.current[id]) return false

    closeRequestedRef.current[id] = true
    dispatch({ type: 'REQUEST_CLOSE', requestId: id })

    return true
  }, [])

  const close = useCallback(
    async (id: string) => {
      const existingClosePromise = closePromisesRef.current[id]
      if (existingClosePromise) {
        await existingClosePromise
        return
      }

      const closePromise = (async () => {
        const modalToClose = modalsRef.current.find((m) => m.id === id)
        if (!modalToClose) return
        if (modalToClose.isClosing) return
        if (finalizedRef.current[id]) return
        if (closeRequestedRef.current[id]) return

        const handler = requestCloseHandlersRef.current[id]

        if (handler) {
          await handler()
          return
        }

        await finalizeClose(id)
      })()

      closePromisesRef.current[id] = closePromise

      try {
        await closePromise
      } finally {
        delete closePromisesRef.current[id]
      }
    },
    [finalizeClose]
  )

  const closeAll = useCallback(() => {
    modalsRef.current.forEach((modal) => {
      delete finalizedRef.current[modal.id]
      delete closeRequestedRef.current[modal.id]
      delete closePromisesRef.current[modal.id]
      delete requestCloseHandlersRef.current[modal.id]

      modal.onClose?.()
    })
    dispatch({ type: 'CLOSE_ALL' })
  }, [])

  const open: ModalContextValue['open'] = useCallback(
    (modal) => {
      const id = modal.id ?? uuid()

      delete finalizedRef.current[id]
      delete closeRequestedRef.current[id]
      delete closePromisesRef.current[id]
      delete requestCloseHandlersRef.current[id]

      const newModal: ModalData = {
        id,
        onClose: modal.onClose,
        close: async () => await close(id),
        component: modal.component,
        props: modal.props,
        isClosing: false,
      }

      dispatch({ type: 'OPEN', modal: newModal })
      return id
    },
    [close]
  )

  const update: ModalContextValue['update'] = useCallback((id, props) => {
    dispatch({ type: 'UPDATE', updateId: id, updateProps: props })
  }, [])

  return (
    <ModalsContext.Provider
      value={{
        modals,
        open,
        close,
        closeAll,
        startClose,
        finalizeClose,
        registerRequestClose,
        update,
      }}
    >
      {children}
    </ModalsContext.Provider>
  )
}

const reducer = (state: ModalData[], action: ModalAction): ModalData[] => {
  switch (action.type) {
    case 'OPEN': {
      if (!action.modal) return state
      return [...state.filter((m) => m.id !== action.modal!.id), action.modal]
    }

    case 'REQUEST_CLOSE': {
      if (!action.requestId) return state
      return state.map((m) => (m.id === action.requestId ? { ...m, isClosing: true } : m))
    }

    case 'FINALIZE_CLOSE': {
      return action.removeId ? state.filter((m) => m.id !== action.removeId) : state
    }

    case 'UPDATE': {
      if (!action.updateId) return state
      return state.map((m) =>
        m.id === action.updateId
          ? { ...m, props: { ...(m.props ?? {}), ...(action.updateProps ?? {}) } }
          : m
      )
    }

    case 'CLOSE_ALL': {
      return []
    }

    default:
      return state
  }
}

interface UseModal {
  modals: ModalData[]
  hasActiveModal: boolean
  activeModal: ModalData | null
  openModal: ModalContextValue['open']
  closeModal: ModalContextValue['close']
  closeAllModals: ModalContextValue['closeAll']
  startCloseModal: ModalContextValue['startClose']
  finalizeClose: ModalContextValue['finalizeClose']
  registerRequestClose: ModalContextValue['registerRequestClose']
  updateModal: ModalContextValue['update']
}

export const useModals = (): UseModal => {
  const context = useContext(ModalsContext)
  if (!context) throw new Error('useModals must be used within a ModalsContextProvider')

  const modals = context.modals

  const active = modals[modals.length - 1] ?? null

  return {
    hasActiveModal: modals.length > 0,
    activeModal: active,
    modals,
    openModal: context.open,
    closeModal: context.close,
    closeAllModals: context.closeAll,
    startCloseModal: context.startClose,
    finalizeClose: context.finalizeClose,
    registerRequestClose: context.registerRequestClose,
    updateModal: context.update,
  }
}

export const useModalsOptional = (): ModalContextValue | undefined => {
  return useContext(ModalsContext)
}

export function useModal<M extends BaseModalType<any>>(
  modal: M,
  defaultProps?: Partial<ModalOpenProps<M>> | (() => Partial<ModalOpenProps<M>>)
) {
  const { openModal, closeModal, updateModal, modals } = useModals()
  const [modalId, setModalId] = useState<string | null>(null)
  const modalIdRef = useRef<string | null>(null)
  const overridesRef = useRef<Partial<ModalOpenProps<M>>>({})
  const defaultPropsRef = useRef<typeof defaultProps>(defaultProps)
  const hasAutoOpenedRef = useRef(false)

  useEffect(() => {
    defaultPropsRef.current = defaultProps
  }, [defaultProps])

  const resolveDefaultProps = useCallback(() => {
    const dp = defaultPropsRef.current
    return typeof dp === 'function' ? dp() : dp
  }, [])

  const open = useCallback(
    (overrides?: Partial<ModalOpenProps<M>>) => {
      const resolvedDefaults = resolveDefaultProps()
      overridesRef.current = overrides || {}
      const mergedProps = { ...resolvedDefaults, ...overrides }

      const { onClose, ...userProps } = mergedProps

      const handleOnClose = () => {
        setModalId(null)
        modalIdRef.current = null
        onClose?.()
      }

      const id = openModal<ModalUserProps<M>>({
        component: modal,
        onClose: handleOnClose,
        props: userProps as ModalUserProps<M>,
      })

      setModalId(id)
      modalIdRef.current = id
      return id
    },
    [resolveDefaultProps, modal, openModal]
  )

  const matchedInstance = useMemo(() => {
    for (let i = modals.length - 1; i >= 0; i--) {
      if (modals[i].component === modal) return modals[i]
    }
    return null
  }, [modals, modal])

  const resolvedId = modalIdRef.current ?? matchedInstance?.id ?? null

  const close = useCallback(async () => {
    const idToClose = modalIdRef.current ?? matchedInstance?.id
    if (idToClose) await closeModal(idToClose)
  }, [closeModal, matchedInstance])

  const isReallyOpen =
    !!matchedInstance || (!!resolvedId && modals.some((m) => m.id === resolvedId))

  const reRender = useCallback(() => {
    const idToUpdate = modalIdRef.current ?? matchedInstance?.id
    if (!idToUpdate) return

    const resolvedDefaults = resolveDefaultProps()
    const mergedProps = { ...(resolvedDefaults ?? {}), ...overridesRef.current }

    updateModal(idToUpdate, mergedProps)
  }, [matchedInstance?.id, resolveDefaultProps, updateModal])

  useEffect(() => {
    if (hasAutoOpenedRef.current) return
    if (isReallyOpen) return

    const resolvedDefaults = resolveDefaultProps()
    if (resolvedDefaults?.initialIsOpen) {
      hasAutoOpenedRef.current = true
      open()
    }
  }, [isReallyOpen, resolveDefaultProps, open])

  return useMemo(
    () => ({
      open,
      close,
      baseProps: defaultProps,
      lastId: modalId,
      currentId: resolvedId,
      isOpen: isReallyOpen,
      reRender,
    }),
    [open, close, defaultProps, modalId, resolvedId, isReallyOpen, reRender]
  )
}
